import type {
  CardColumnConfig,
  CardColumnId,
  CardId,
  ConversationBoardCard,
} from '@voll-intelligence/domain';

import type { ConversationBoardResponse } from '@web/data/repositories';
import { t } from '@web/i18n';
import { EmptyHint } from '@web/ui/core';

import { cardColumnTone } from '@web/ui/core/utils/board_tone';
import { ColumnHead } from './column_head';
import { ConversationCard } from './conversation_card';

const COLUMN_HINT_KEY: Record<CardColumnId, Parameters<typeof t>[0]> = {
  backlog: 'board.column.hint.backlog',
  in_progress: 'board.column.hint.in_progress',
  resolved: 'board.column.hint.resolved',
};

export interface ConversationsBoardProps {
  data: ConversationBoardResponse;
  /**
   * Per-tenant column configuration (stable IDs + tenant labels). Source
   * of truth is `OrgBoardConfig`, fetched once at the page level so both
   * boards share a single network call.
   */
  cardColumns: readonly CardColumnConfig[];
  selectedCardId: CardId | null;
  onSelect: (cardId: CardId) => void;
}

/**
 * Conversations board — 3 columns rendered from a `ConversationBoardResponse`.
 * Labels come from `cardColumns` (per-org), tone is derived from the
 * stable column ID in `cardColumnTone` (design-system concern).
 */
export function ConversationsBoard({
  data,
  cardColumns,
  selectedCardId,
  onSelect,
}: Readonly<ConversationsBoardProps>): React.JSX.Element {
  return (
    <div className="grid grid-cols-board gap-4 items-start" data-testid="conversations-board">
      {cardColumns.map((col) => {
        const cards = data.columns[col.id];
        return (
          <section key={col.id} className="flex flex-col gap-2.5 min-w-0">
            <ColumnHead
              tone={cardColumnTone(col.id)}
              label={col.label}
              count={cards.length}
              hint={t(COLUMN_HINT_KEY[col.id])}
            />
            <div className="flex flex-col gap-2.5 min-h-board-col">
              {cards.length === 0 ? (
                <EmptyHint
                  density="compact"
                  className="flex min-h-card items-center justify-center"
                >
                  {t('workspace.empty.column')}
                </EmptyHint>
              ) : (
                cards.map((card: ConversationBoardCard) => (
                  <ConversationCard
                    key={card.id}
                    card={card}
                    selected={selectedCardId === card.id}
                    onClick={() => {
                      onSelect(card.id);
                    }}
                  />
                ))
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
